import { Channel, Socket } from "phoenix";

const VOICE_CHANNEL_TOPIC = "voice:lobby";
const SEND_EVENT = "send";
const MAX_RECONNECT_TRIES_BEFORE_FAIL = 5;
const RECONNECT_DELAY_MS = 2_000;
const RECONNECT_DELAY_AFTER_THRESHOLD_MS = 10_000;

export type VoiceModeTransportStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "failed";

export type VoiceModeIncomingEvent =
  | { type: "text"; data: string }
  | { type: "audio"; id: string; format: string; data: string }
  | { type: "error"; data: string }
  | { type: "turn"; data: { agent: string; user: string } }
  | { type: "interrupt" }
  | { type: "end_call" };

export interface VoiceModeTransportConfig {
  /** Full Phoenix socket endpoint, e.g. `wss://host/voice`. */
  url: string;
  /** URL query parameters sent to the Phoenix socket. */
  params: Record<string, string>;
  /** Fired for every payload received on the voice channel. */
  onEventReceived: (event: VoiceModeIncomingEvent) => void;
  /** Fired whenever connection status changes. */
  onStatusChange?: (status: VoiceModeTransportStatus) => void;
}

export class VoiceModeTransport {
  private readonly url: VoiceModeTransportConfig["url"];
  private readonly params: VoiceModeTransportConfig["params"];
  private readonly onEventReceived: VoiceModeTransportConfig["onEventReceived"];
  private readonly onStatusChange?: VoiceModeTransportConfig["onStatusChange"];

  private status: VoiceModeTransportStatus;
  private socket: Socket | null;
  private channel: Channel | null;

  /** Distinguishes intentional disconnect from unexpected network issues. */
  private isIntentionalDisconnect: boolean;

  constructor(config: VoiceModeTransportConfig) {
    this.url = config.url;
    this.params = config.params;
    this.onStatusChange = config.onStatusChange;
    this.onEventReceived = config.onEventReceived;

    this.status = "idle";
    this.socket = null;
    this.channel = null;
    this.isIntentionalDisconnect = false;
  }

  /**
   * Returns the latest connection status.
   */
  public getStatus(): VoiceModeTransportStatus {
    return this.status;
  }

  /**
   * Opens the Phoenix socket and joins the voice channel.
   * No-ops when a connection attempt is already in flight or connected.
   */
  public connect(): void {
    if (this.status === "connecting" || this.status === "connected") {
      return;
    }

    if (this.status !== "reconnecting") {
      this.setStatus("connecting");
    }

    this.isIntentionalDisconnect = false;
    this.teardownSocket();

    const socket = new Socket(this.url, {
      params: this.params,
      reconnectAfterMs: (tries: number) => {
        if (tries > MAX_RECONNECT_TRIES_BEFORE_FAIL) {
          this.setStatus("failed");
        }

        return tries >= MAX_RECONNECT_TRIES_BEFORE_FAIL
          ? RECONNECT_DELAY_AFTER_THRESHOLD_MS
          : RECONNECT_DELAY_MS;
      },
    });

    socket.onOpen(() => {
      this.setStatus("connected");
    });

    socket.onError(() => {
      if (this.isIntentionalDisconnect) {
        return;
      }

      if (this.status !== "failed") {
        this.setStatus("reconnecting");
      }
    });

    socket.onClose(() => {
      if (this.isIntentionalDisconnect) {
        return;
      }

      if (this.status === "connected") {
        this.setStatus("reconnecting");
      }
    });

    this.socket = socket;
    socket.connect();

    const channel = socket.channel(VOICE_CHANNEL_TOPIC);
    this.channel = channel;

    channel.onMessage = (event: string, payload: unknown) => {
      if (event === "response_stream") {
        this.onEventReceived?.(payload as VoiceModeIncomingEvent);
      }

      return payload;
    };

    channel
      .join()
      .receive("error", () => {
        // Join failure is unrecoverable without a full reconnect.
        this.setStatus("failed");
      })
      .receive("timeout", () => {
        this.setStatus("failed");
      });
  }

  /**
   * Leaves the channel and disconnects the socket, returning to idle.
   */
  public disconnect(): void {
    this.isIntentionalDisconnect = true;
    this.teardownSocket();
    this.setStatus("idle");
  }

  /**
   * Closes the socket and tries to connect again.
   * Used for UI "Retry" after a failure or reconnect issue.
   */
  public retry(): void {
    this.isIntentionalDisconnect = true;
    this.teardownSocket();
    this.isIntentionalDisconnect = false;
    this.setStatus("reconnecting");
    this.connect();
  }

  /**
   * Fire-and-forget: sends text to the voice channel.
   * Throws if the channel isn't connected or text is empty.
   */
  public sendText(text: string): void {
    const trimmedText = text.trim();

    if (trimmedText.length === 0) {
      throw new Error("Cannot send empty text");
    }

    if (!this.channel) {
      throw new Error("Voice mode channel is not connected");
    }

    this.channel.push(SEND_EVENT, { prompt: trimmedText });
  }

  /**
   * For sending audio. Not implemented yet.
   */
  // oxlint-disable-next-line class-methods-use-this
  public sendAudio(): void {
    throw new Error("sendAudio is not implemented");
  }

  /**
   * Updates status and notifies the optional listener when the value changes.
   */
  private setStatus(nextStatus: VoiceModeTransportStatus): void {
    if (this.status === nextStatus) {
      return;
    }

    this.status = nextStatus;
    this.onStatusChange?.(nextStatus);
  }

  /**
   * Leaves the channel and disconnects the socket.
   */
  private teardownSocket(): void {
    if (this.channel) {
      this.channel.leave();
      this.channel = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}
